import {
  QueryFunction,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from 'react-query'
import {useInfiniteQueryUntilEnd} from '../hooks/useInfiniteQueryUntilEnd'
import {http} from '../services/api/http'
import {fetchDivisions as fetchDivisionsEndpoint} from '../services/coreApi/divisions'
import {useRequiredAuth} from '../stores/AuthStore'

type Division = {
  ID: string
  clientID: string
  self: string
  name: string
  description: string
}
type QueryFnData = {
  results: Division[]
  totalCount: number
  nextPage: string
}
type Client = ReturnType<typeof useRequiredAuth>['client'] // I think there is a better way with Generics, but don't have time to figure it out
type GetQueryOptions = (client: Client) => UseInfiniteQueryOptions<QueryFnData>
type QueryFn = (client: Client) => QueryFunction<QueryFnData>

const PAGE_SIZE = 100
const QUERY_STALE_TIME = 1000 * 60 * 30 // 30 minutes

const queryKeys = {
  all: () => [{resource: 'divisions'}] as const, // [{ resource: 'division'}]
  lists: () => [{...queryKeys.all()[0], type: 'list'}] as const, // [{ resource: 'division', type: 'list}]
  list: () => [{...queryKeys.all()[0], type: 'list'}] as const, // [{ resource: 'division', type: 'list}] // same as above but might start having filters added
}

const queryFn: QueryFn =
  (client) =>
  async ({pageParam = 1}) => {
    const response = await fetchDivisionsEndpoint(http, {
      clientID: client.ID,
      pageNumber: pageParam,
      pageSize: PAGE_SIZE,
    })

    const {Results, TotalCount, Next} = response.data

    return {
      results: Results.map((result) => ({
        ID: result.ID.toString(),
        clientID: result.ClientID.toString(),
        self: result.Self,
        name: result.Name,
        description: result.Description,
      })),
      totalCount: TotalCount,
      nextPage: Next,
    }
  }

const getQueryOptions: GetQueryOptions = (client) => ({
  queryKey: queryKeys.list(),
  queryFn: queryFn(client),
  staleTime: QUERY_STALE_TIME,
  getNextPageParam: (lastPage, allPages) =>
    // If the last page has some real next URL string, then set the pageParam to the next page
    lastPage.nextPage !== '' ? allPages.length + 1 : undefined,
})

type UseDivisions = (params?: {
  enabled?: boolean
}) => UseInfiniteQueryResult<QueryFnData>

export const useDivisions: UseDivisions = ({enabled = true} = {}) => {
  const {client} = useRequiredAuth()

  return useInfiniteQueryUntilEnd<QueryFnData>({
    ...getQueryOptions(client),
    enabled,
  })
}
